/**
 * Shared traffic-light tone mappings for optical signal and port/ONU
 * state, used by BOTH the desktop inventory tables
 * (``pages/chassis/ports/*``) and the mobile field-tech cards
 * (``mobile/MChassisPorts.tsx``) so the two surfaces can never drift.
 */
import type { BadgeTone } from "../components/Badge";

/**
 * GPON downstream RX power (dBm, negative). The healthy window is
 * -25..-10; the -28/-8 outer band leaves a warning margin on each side
 * before the signal is outright out of spec.
 */
export function rxTone(rx: number | null): BadgeTone {
  if (rx == null) return "muted";
  if (rx < -28 || rx > -8) return "danger";
  if (rx < -25 || rx > -10) return "warning";
  return "success";
}

/**
 * GPON ONU state pill. ``missing`` (the ONU vanished from the last
 * ``missing_scans`` inventory sweeps) outranks the stored state — the
 * row is stale until it either reappears or is retired.
 */
export function gponStateTone(
  state: string | null,
  missingScans?: number | null,
): BadgeTone {
  if (state === "up") return "success";
  if ((missingScans ?? 0) > 0) return "warning";
  return "neutral";
}

/** Huawei VDSL line states, verbatim from the OLT. */
export const VDSL_STATE_TONE: Record<string, BadgeTone> = {
  Activated: "success",
  Activating: "info",
  Disabled: "muted",
  Loopback: "warning",
};

/**
 * VDSL line error-health flag (``vdsl_inventory.error_health``, computed
 * daily by the worker from 7-day ES/SES/UAS/FEC/retrain totals — see
 * ``chassis_shared.vdsl_line_health``). The VDSL counterpart of rxTone.
 */
export type VdslErrorHealth = "good" | "warning" | "critical" | "unknown";

/** 7-day window summary persisted as ``vdsl_inventory.errors_7d``. */
export type VdslErrors7d = {
  es?: number;
  ses?: number;
  uas?: number;
  fec_s?: number;
  los_failures?: number;
  resets?: number;
  retrains?: number;
  samples?: number;
  days_covered?: number;
  reasons?: string[];
};

export function vdslErrorTone(health: string | null | undefined): BadgeTone {
  if (health === "critical") return "danger";
  if (health === "warning") return "warning";
  if (health === "good") return "success";
  return "muted";
}

/** Only warning/critical get a chip — good/unknown stay quiet, like the
 *  GPON RX pill only shouting when out of band. */
export function isVdslErrorFlagged(health: string | null | undefined): boolean {
  return health === "warning" || health === "critical";
}

/** Short chip label for a flagged port. */
export function vdslErrorLabel(health: string | null | undefined): string {
  if (health === "critical") return "errors!";
  if (health === "warning") return "errors";
  return "";
}

/** Tooltip text: 7-day totals plus the reasons the flag tripped. */
export function vdslErrorTooltip(
  health: string | null | undefined,
  errors: VdslErrors7d | null | undefined,
): string {
  const head = `Line errors: ${health ?? "not evaluated"}`;
  if (!errors) return head;
  const n = (v: number | undefined) => (v ?? 0).toLocaleString("en-AU");
  const days = errors.days_covered ?? 0;
  const lines = [
    `${head} (last ${days.toFixed(1)} day${days === 1 ? "" : "s"})`,
    `ES ${n(errors.es)} · SES ${n(errors.ses)} · UAS ${n(errors.uas)}`,
    `FEC seconds ${n(errors.fec_s)} · retrains ${n(errors.retrains)}` +
      (errors.resets ? ` (${errors.resets} counter reset${errors.resets === 1 ? "" : "s"})` : ""),
  ];
  for (const r of errors.reasons ?? []) lines.push(`• ${r}`);
  return lines.join("\n");
}
